import {
  daysBetween,
  formatTime,
  LAUNCH_DATE,
  type LeaderboardResponse,
  londonDateString,
  MAX_TOTAL,
} from '@geosport/shared';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../components/Button';
import { DayNav } from '../components/DayNav';
import { Shell } from '../components/Shell';
import { Toast } from '../components/Toast';
import { track } from '../lib/analytics';
import { Link } from '../lib/router';
import { shareText } from '../lib/share';
import { useApi } from '../lib/useApi';
import { useRound } from '../store/round';

const WINDOW_DAYS = 14;
const FIRST_PAGE = 10;

const MEDAL: Record<number, string> = { 1: '🏆', 2: '🥈', 3: '🥉' };

export function Leaderboard() {
  const today = londonDateString();
  const [date, setDate] = useState(today);
  // "Show more" is remembered per day, so switching days starts collapsed again.
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const showAll = expandedDate === date;
  const { data, error, loading, reload } = useApi<LeaderboardResponse>(`/leaderboard/${date}`);
  const age = daysBetween(date, today);
  const summary = useRound((s) => s.daily?.summary ?? null);
  const [toast, setToast] = useState<string | null>(null);
  const clearToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    track('leaderboard_viewed', { date });
  }, [date]);

  const entries = data ? (showAll ? data.entries : data.entries.slice(0, FIRST_PAGE)) : [];

  return (
    <Shell title="Leaderboard">
      <DayNav
        date={date}
        onChange={setDate}
        canBack={age < WINDOW_DAYS && date > LAUNCH_DATE}
        canForward={age > 0}
        subtitle={
          data
            ? `Puzzle #${data.puzzleNo} · ${data.players} ${data.players === 1 ? 'player' : 'players'}${
                data.avgTotal !== null ? ` · avg ${data.avgTotal}` : ''
              }`
            : undefined
        }
      />

      {error && (
        <div className="rounded-2xl bg-red-500/10 p-4 text-sm text-red-200 ring-1 ring-red-500/30">
          <p className="mb-2">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="min-h-11 rounded-lg bg-white/10 px-4 font-semibold"
          >
            Retry
          </button>
        </div>
      )}
      {loading && !data && (
        <div className="flex flex-col gap-2" aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="skeleton h-11 rounded-xl" />
          ))}
        </div>
      )}

      {data && (
        <>
          {data.me && (
            <div className="mb-3 flex items-center justify-between rounded-2xl bg-accent/15 p-3 ring-1 ring-accent/40">
              <div>
                <div className="text-xs font-semibold tracking-wide text-accent uppercase">You</div>
                <div className="text-sm text-white/70">
                  {data.me.rank ? `#${data.me.rank}` : 'Outside the top 50'}
                  {data.me.betterThanPct !== null && ` · better than ${data.me.betterThanPct}%`}
                </div>
              </div>
              <div className="text-right tabular-nums">
                <div className="text-xl font-bold">{data.me.total}</div>
                <div className="text-xs text-white/60">⏱ {formatTime(data.me.timeMs)}</div>
              </div>
            </div>
          )}

          {data.entries.length === 0 ? (
            <p className="rounded-2xl bg-white/5 p-4 text-white/60">
              No scores yet{age === 0 ? '. Be the first!' : ' for this day.'}
            </p>
          ) : (
            <ol className="divide-y divide-white/5 rounded-2xl bg-white/5">
              {entries.map((e) => (
                <li
                  key={e.rank}
                  className={`flex items-center gap-3 px-3 py-2 text-sm ${
                    e.isMe ? 'bg-accent/10 font-semibold' : ''
                  } ${e.rank === 1 ? 'text-accent' : ''}`}
                >
                  <span className="w-9 text-white/50 tabular-nums">{MEDAL[e.rank] ?? e.rank}</span>
                  <span className="flex-1 truncate">{e.displayName}</span>
                  <span className="font-bold tabular-nums">{e.total.toLocaleString('en-GB')}</span>
                  <span className="w-12 text-right text-xs text-white/50 tabular-nums">
                    {formatTime(e.timeMs)}
                  </span>
                </li>
              ))}
            </ol>
          )}

          {!showAll && data.entries.length > FIRST_PAGE && (
            <div className="mt-3 flex justify-center">
              <Button variant="secondary" onClick={() => setExpandedDate(date)}>
                Show more
              </Button>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button
              disabled={!summary}
              onClick={() =>
                summary &&
                void shareText(summary.shareText).then((o) =>
                  setToast(
                    o === 'copied'
                      ? 'Copied to clipboard ✓'
                      : o === 'failed'
                        ? 'Could not share'
                        : null,
                  ),
                )
              }
            >
              Share score
            </Button>
            <Button
              variant="secondary"
              onClick={() => (window.history.length > 1 ? window.history.back() : undefined)}
            >
              Back
            </Button>
          </div>
          <p className="mt-4 text-center text-xs text-white/40">
            Top 50 of the day, ties broken by time. Max {MAX_TOTAL} points.{' '}
            <Link to="/groups" className="text-accent underline underline-offset-2">
              Want a private board? Make a group.
            </Link>
          </p>
        </>
      )}
      <Toast message={toast} onDone={clearToast} />
    </Shell>
  );
}
